import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import type {
  EmailMessageReceivedEventData,
  EmailMessageSentEventData,
} from '@super/shared-types'
import { ThreadList } from './components/ThreadList'
import { MessageList } from './components/MessageList'
import { ChatSkeleton } from './components/Skeletons/ChatSkeleton'
import { EmailDrawer } from './components/EmailDrawer'
import { SessionDetailsDrawer } from './components/SessionDetailsDrawer/SessionDetailsDrawer'
import { useChatData } from './hooks/useChatData'
import { getContactDisplayInfo } from './utils/contact-helpers'
import { Avatar } from '@/components/common/Avatar'
import {
  getDeeplinkActionType,
  DeeplinkActionType,
} from './utils/search-match-detection'
import { useResolvedThreadAppAuth } from '@/hooks/useResolvedThreadAppAuth.js'

interface ChatProps {
  initialThreadId?: string
  scrollTo?: 'end' | string | undefined
  sessionId?: string
}

export const Chat: React.FC<ChatProps> = ({
  initialThreadId,
  scrollTo,
  sessionId,
}) => {
  const {
    threads,
    selectedThreadId,
    selectedThreadDetails,
    messages,
    hasMore,
    threadsLoading,
    messagesLoading,
    loadingMore,
    filter,
    searchQuery,
    searchDeeplinkEventId,
    searchResultSnippets,
    searchResultMetadata,
    isThreadActive,
    hasAttemptedLoad,
    hasMoreNewer,
    isDeeplinked,
    setSelectedThread,
    setFilter,
    setSearchQuery,
    setSearchFilters,
    loadMoreMessagesForThread,
    loadEventsAroundMessage,
    loadMoreNewerMessages,
    sessionScrollTarget,
  } = useChatData(initialThreadId, sessionId)

  const { isSuperAdmin } = useResolvedThreadAppAuth({
    threadId: initialThreadId,
  })

  const selectedThread = threads.find(t => t.id === selectedThreadId)

  // Use search deeplink if available, then session scroll target, otherwise use the scrollTo prop from route
  const effectiveScrollTo =
    searchDeeplinkEventId || sessionScrollTarget || scrollTo

  // Compute deeplink action type based on search context
  let deeplinkActionType = DeeplinkActionType.SCROLL
  if (searchDeeplinkEventId && searchQuery && selectedThreadId) {
    // Find the composite ID that corresponds to this thread
    // The metadata map uses composite IDs (threadId-sessionId) as keys
    const compositeId = Array.from(searchResultMetadata.keys()).find(key => {
      const metadata = searchResultMetadata.get(key)
      return metadata?.originalThreadId === selectedThreadId
    })

    if (compositeId) {
      const metadata = searchResultMetadata.get(compositeId)
      if (metadata) {
        deeplinkActionType = getDeeplinkActionType(
          metadata.eventType,
          searchQuery,
          metadata,
        )
      }
    }
  }

  const [emailDrawerOpen, setEmailDrawerOpen] = useState(false)
  const [selectedEmail, setSelectedEmail] = useState<
    EmailMessageReceivedEventData | EmailMessageSentEventData | null
  >(null)

  const [sessionDetailsDrawerOpen, setSessionDetailsDrawerOpen] =
    useState(false)
  const [sessionDetailsSessionId, setSessionDetailsSessionId] = useState<
    string | null
  >(null)

  const handleSessionDetailsClick = (sessionId: string) => {
    setSessionDetailsSessionId(sessionId)
    setSessionDetailsDrawerOpen(true)
  }

  const handleEmailClick = (
    emailData: EmailMessageReceivedEventData | EmailMessageSentEventData,
  ) => {
    setSelectedEmail(emailData)
    setEmailDrawerOpen(true)
  }

  // Auto-open drawer for SCROLL_AND_DRAWER action (e.g., email quotedText matches)
  useEffect(() => {
    if (
      deeplinkActionType === DeeplinkActionType.SCROLL_AND_DRAWER &&
      searchDeeplinkEventId
    ) {
      // Find the message with the matching event ID
      const targetMessage = messages.find(
        msg => msg.id === searchDeeplinkEventId,
      )

      if (targetMessage && targetMessage.data) {
        // Open email drawer with the event data (scrolling is handled by MessageList)
        setSelectedEmail(targetMessage.data as EmailMessageReceivedEventData)
        setEmailDrawerOpen(true)
      }
    }
  }, [deeplinkActionType, searchDeeplinkEventId, messages])

  // Show skeleton while loading threads (prevents empty state flash)
  if (threads.length === 0 && (threadsLoading || !hasAttemptedLoad)) {
    return <ChatSkeleton />
  }

  return (
    <div className="flex h-full w-full bg-white dark:bg-background overflow-hidden">
      <ThreadList
        threads={threads}
        selectedThreadId={selectedThreadId || undefined}
        onThreadSelect={setSelectedThread}
        filter={filter}
        loading={threadsLoading}
        searchQuery={searchQuery}
        searchResultSnippets={searchResultSnippets}
        searchResultMetadata={searchResultMetadata}
        onSearchChange={setSearchQuery}
        onFilterChange={setFilter}
        onFiltersChange={setSearchFilters}
      />

      <div
        className={`flex flex-col flex-1 h-full bg-white dark:bg-background overflow-hidden transition-opacity duration-200 ${
          searchQuery && !isThreadActive ? 'opacity-40' : 'opacity-100'
        }`}
      >
        <AnimatePresence mode="wait">
          {selectedThread && (
            <motion.div
              key={selectedThread.id}
              className="h-16 min-h-16 px-6 border-b border-gray-200 dark:border-border flex items-center justify-between bg-white dark:bg-background flex-shrink-0"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              {(() => {
                const { title, subtitle } = getContactDisplayInfo(
                  selectedThread,
                  selectedThreadDetails,
                )

                return (
                  <div className="flex items-center gap-3">
                    <Avatar
                      email={selectedThread.participant.email}
                      name={selectedThread.participant.name}
                      size={40}
                    />

                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-foreground">
                        {title}
                      </h3>
                      {subtitle && (
                        <p className="text-sm text-gray-500 dark:text-muted-foreground">
                          {subtitle}
                        </p>
                      )}
                    </div>
                  </div>
                )
              })()}

              {/* <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                    aria-label="More options"
                  >
                    <MoreVertical className="h-4 w-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={() => handleMenuAction('change-status')}
                  >
                    Change status
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => handleMenuAction('view-contact')}
                  >
                    View contact
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu> */}
            </motion.div>
          )}
        </AnimatePresence>

        <MessageList
          threadId={selectedThreadId || ''}
          messages={messages}
          onLoadMore={loadMoreMessagesForThread}
          loadEventsAroundMessage={loadEventsAroundMessage}
          hasMore={hasMore}
          loading={messagesLoading || loadingMore}
          scrollTo={effectiveScrollTo}
          searchQuery={searchQuery}
          onEmailClick={handleEmailClick}
          onSessionDetailsClick={
            isSuperAdmin ? handleSessionDetailsClick : undefined
          }
          isSuperAdmin={isSuperAdmin}
          companyId={selectedThread?.company.id}
          hasMoreNewer={hasMoreNewer}
          isDeeplinked={isDeeplinked}
          loadMoreNewerMessages={loadMoreNewerMessages}
        />
      </div>

      <EmailDrawer
        open={emailDrawerOpen}
        onOpenChange={setEmailDrawerOpen}
        emailData={selectedEmail}
      />

      {isSuperAdmin && (
        <SessionDetailsDrawer
          open={sessionDetailsDrawerOpen}
          onOpenChange={setSessionDetailsDrawerOpen}
          sessionId={sessionDetailsSessionId}
          companyId={selectedThread?.company.id}
        />
      )}
    </div>
  )
}

export default Chat
